import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { Observable, BehaviorSubject, filter, takeUntil, Subject, map } from 'rxjs';
import { GoogleAuthService } from './services/google-auth.service';
import { JwtUtilsService } from './services/jwt-utils.service';
import { VericureUser } from './models/vericure-user';
import { ActiveCustomer } from './models/active-customer';

const ROLES_STORAGE_KEY = 'vericureUserRoles';
const ACTIVE_CUSTOMER_KEY = 'vericureActiveCustomer';
const AVAILABLE_CUSTOMERS_KEY = 'vericureAvailableCustomers';

@Injectable({
  providedIn: 'root',
})
export class UserAuthService {
  private _isAuthenticated = new BehaviorSubject<boolean>(false);
  isAuthenticated$: Observable<boolean> = this._isAuthenticated.asObservable();
  private _activeAccount = new BehaviorSubject<AccountInfo | null>(null);
  activeAccount$: Observable<AccountInfo | null> =
    this._activeAccount.asObservable();

  UserName: string = '';
  loginDisplay = false;
  tokenExpiration: string = '';
  CustomerTenantId: string = '';
  authProvider: string = ''; // Track which provider is being used
  private readonly _destroying$ = new Subject<void>();

  private _userRoles = new BehaviorSubject<string[]>(this.loadCachedRoles());
  userRoles$: Observable<string[]> = this._userRoles.asObservable();
  isAdmin$: Observable<boolean> = this._userRoles.pipe(
    map((roles) => roles.includes('administrator') || roles.includes('SuperAdmin'))
  );
  isSuperAdmin$: Observable<boolean> = this._userRoles.pipe(
    map((roles) => roles.includes('SuperAdmin'))
  );

  private _activeCustomer = new BehaviorSubject<ActiveCustomer | null>(this.loadActiveCustomer());
  activeCustomer$: Observable<ActiveCustomer | null> = this._activeCustomer.asObservable();

  availableCustomers: ActiveCustomer[] = this.loadAvailableCustomers();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private googleAuthService: GoogleAuthService,
    private jwtUtils: JwtUtilsService
  ) {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      });

    // Used for storing and displaying token expiration
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        )
      )
      .subscribe((msg) => {
        this.tokenExpiration = (msg.payload as any).expiresOn;
        localStorage.setItem('tokenExpiration', this.tokenExpiration);
      });

    // Initialize authentication state on service startup
    // Only call setLoginDisplay after checking if MSAL is ready
    this.initializeAuthState();
  }

  /**
   * Set the user's roles from the Cosmos VericureUsers document returned by CheckUserExists.
   * Roles are persisted to localStorage so they survive page reloads while logged in.
   */
  setRolesFromCosmos(user: VericureUser | null): void {
    const roles = user?.roles ?? [];
    this._userRoles.next(roles);
    try {
      localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(roles));
    } catch {
      // ignore storage errors
    }

    // customer-role users have exactly one customer identified in their VericureUser doc.
    // CheckUserExists uses OkObjectResult (camelCase) so the field arrives as "customerId".
    // Cast defensively to also handle any PascalCase variant for robustness.
    const userCustomerId: string = user?.customerId ?? (user as any)?.CustomerId ?? '';
    if (roles.includes('customer') && userCustomerId) {
      this.setActiveCustomer({
        CustomerId: userCustomerId,
        CustomerName: '',
        TenantId: user?.TenantId ?? this.CustomerTenantId,
      });
    }
  }

  private clearRoles(): void {
    this._userRoles.next([]);
    try {
      localStorage.removeItem(ROLES_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  }

  setActiveCustomer(customer: ActiveCustomer): void {
    this._activeCustomer.next(customer);
    try {
      sessionStorage.setItem(ACTIVE_CUSTOMER_KEY, JSON.stringify(customer));
    } catch { /* ignore */ }
  }

  getActiveCustomer(): ActiveCustomer | null {
    return this._activeCustomer.getValue();
  }

  getActiveCustomerId(): string {
    return this._activeCustomer.getValue()?.CustomerId ?? '';
  }

  setAvailableCustomers(customers: ActiveCustomer[]): void {
    this.availableCustomers = customers;
    try {
      sessionStorage.setItem(AVAILABLE_CUSTOMERS_KEY, JSON.stringify(customers));
    } catch { /* ignore */ }
  }

  private clearActiveCustomer(): void {
    this._activeCustomer.next(null);
    this.availableCustomers = [];
    try {
      sessionStorage.removeItem(ACTIVE_CUSTOMER_KEY);
      sessionStorage.removeItem(AVAILABLE_CUSTOMERS_KEY);
    } catch { /* ignore */ }
  }

  private isValidCustomer(c: ActiveCustomer | null | undefined): boolean {
    return !!c && !!c.CustomerId && !!c.TenantId;
  }

  private loadActiveCustomer(): ActiveCustomer | null {
    try {
      const stored = sessionStorage.getItem(ACTIVE_CUSTOMER_KEY);
      if (!stored) return null;
      const parsed = JSON.parse(stored) as ActiveCustomer;
      // Discard malformed cache (e.g. pre-fix entries missing ids) so the app re-fetches.
      if (!this.isValidCustomer(parsed)) {
        sessionStorage.removeItem(ACTIVE_CUSTOMER_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private loadAvailableCustomers(): ActiveCustomer[] {
    try {
      const stored = sessionStorage.getItem(AVAILABLE_CUSTOMERS_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      // Drop malformed entries; if any are bad, discard the whole cache to force a clean re-fetch.
      if (!parsed.every((c) => this.isValidCustomer(c))) {
        sessionStorage.removeItem(AVAILABLE_CUSTOMERS_KEY);
        return [];
      }
      return parsed;
    } catch {
      return [];
    }
  }

  private loadCachedRoles(): string[] {
    try {
      const cached = localStorage.getItem(ROLES_STORAGE_KEY);
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  getRoles(): string[] {
    return this._userRoles.getValue();
  }

  hasRole(role: string): boolean {
    return this._userRoles.getValue().includes(role);
  }

  // Safely initialize authentication state
  private initializeAuthState(): void {
    try {
      // Try to set login display, but handle the case where MSAL might not be ready
      this.setLoginDisplay();
    } catch (error) {
      console.log('UserAuthService > MSAL not yet initialized, will retry when ready');
      // The inProgress$ subscription will handle this when MSAL is ready
    }
  }

  // If the user is logged in, present the user with a "logged in" experience
  setLoginDisplay() {
    try {
      const accounts = this.msalService.instance.getAllAccounts();

      if (accounts && accounts.length > 0) {
        // Microsoft/MSAL authentication
        this._activeAccount.next(accounts[0]);
        this._isAuthenticated.next(true);
        this.UserName = accounts[0].username;
        this.authProvider = 'microsoft';
        
        // Extract tenant ID from the account
        this.CustomerTenantId = this.getCustomerTenantIdFromAccount(accounts[0]);
        
        console.log('UserAuthService > Microsoft Account:', accounts[0]);
        console.log('UserAuthService > Customer Tenant ID:', this.CustomerTenantId);
      } else {
        // Check for localStorage authentication (email/password or Google login)
        const localStorageAuth = localStorage.getItem('isAuthenticated');
        const accessToken = localStorage.getItem('accessToken');
        const username = localStorage.getItem('username');
        const customerTenantId = localStorage.getItem('customerTenantId');
        const authProvider = localStorage.getItem('authProvider') || 'email';
        
        if (localStorageAuth === 'true' && accessToken) {
          // User is authenticated via email/password or Google
          this._isAuthenticated.next(true);
          this.UserName = username || '';
          this.CustomerTenantId = customerTenantId || '';
          this.authProvider = authProvider;
          this._activeAccount.next(null); // No MSAL account for non-Microsoft login
          
          if (authProvider === 'google') {
            console.log('UserAuthService > Google authentication found');
            // Additional Google-specific data
            const googleUserName = localStorage.getItem('googleUserName');
            const googleUserPicture = localStorage.getItem('googleUserPicture');
            console.log('UserAuthService > Google User Name:', googleUserName);
            console.log('UserAuthService > Google User Picture:', googleUserPicture);
          } else {
            console.log('UserAuthService > Email/password authentication found');
          }
          
          console.log('UserAuthService > Customer Tenant ID from localStorage:', this.CustomerTenantId);
          console.log('UserAuthService > Auth Provider:', this.authProvider);
        } else {
          this._activeAccount.next(null);
          this._isAuthenticated.next(false);
          this.CustomerTenantId = '';
          this.authProvider = '';
          console.log('UserAuthService > No accounts found.');
        }
      }
    } catch (error) {
      // If MSAL is not initialized yet, check localStorage for non-Microsoft auth
      console.log('UserAuthService > MSAL not ready, checking localStorage authentication');
      const localStorageAuth = localStorage.getItem('isAuthenticated');
      const accessToken = localStorage.getItem('accessToken');
      const username = localStorage.getItem('username');
      const customerTenantId = localStorage.getItem('customerTenantId');
      const authProvider = localStorage.getItem('authProvider') || 'email';
      
      if (localStorageAuth === 'true' && accessToken) {
        // User is authenticated via email/password or Google
        this._isAuthenticated.next(true);
        this.UserName = username || '';
        this.CustomerTenantId = customerTenantId || '';
        this.authProvider = authProvider;
        this._activeAccount.next(null);
        
        console.log('UserAuthService > Non-MSAL authentication found');
        console.log('UserAuthService > Auth Provider:', this.authProvider);
      } else {
        this._activeAccount.next(null);
        this._isAuthenticated.next(false);
      }
    }
  }

  // Log the user in and redirect them if MSAL provides a redirect URI otherwise go to the default URI
  login() {
    // if (this.msalGuardConfig.authRequest) {
    //   this.msalService.loginRedirect({
    //     ...this.msalGuardConfig.authRequest,
    //   } as RedirectRequest);
    // } else {
    //   this.msalService.loginRedirect();
    // }
    this.msalService.loginRedirect();
  }

  // Log the user out
  logout() {
    const currentAuthProvider = this.authProvider || localStorage.getItem('authProvider');
    
    // Clear localStorage authentication data for all login types
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('username');
    localStorage.removeItem('customerTenantId');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('authProvider');
    
    // Clear Google-specific data
    localStorage.removeItem('googleUserName');
    localStorage.removeItem('googleUserPicture');

    // Clear cached roles and active customer selection
    this.clearRoles();
    this.clearActiveCustomer();
    
    // Clear Google authentication if user was logged in with Google
    if (currentAuthProvider === 'google') {
      console.log('Logging out from Google authentication');
      this.googleAuthService.logout();
    }
    
    // Clear MSAL authentication (this will redirect for Microsoft users)
    if (currentAuthProvider === 'microsoft' || !currentAuthProvider) {
      console.log('Logging out from Microsoft authentication');
      this.msalService.logoutRedirect();
    } else {
      // For non-Microsoft users, just clear the state and redirect manually
      this._activeAccount.next(null);
      this._isAuthenticated.next(false);
      this.UserName = '';
      this.CustomerTenantId = '';
      this.authProvider = '';
      
      // Redirect to login page
      window.location.href = '/login';
    }
  }

  isAuthenticated(): boolean {
    // Check MSAL authentication first
    const msalAuth = this._isAuthenticated.getValue();
    if (msalAuth) {
      return true;
    }
    
    // Check localStorage authentication for email/password login
    const localStorageAuth = localStorage.getItem('isAuthenticated');
    const accessToken = localStorage.getItem('accessToken');
    
    // Return true if we have both authentication flag and token in localStorage
    return localStorageAuth === 'true' && !!accessToken;
  }

  /**
   * Extract customer tenant ID from the account information
   * @param account - The account info from MSAL
   * @returns The customer tenant ID
   */
  getCustomerTenantIdFromAccount(account: AccountInfo): string {
    // Method 1: From account homeAccountId (format: objectId.tenantId)
    if (account.homeAccountId && account.homeAccountId.includes('.')) {
      const parts = account.homeAccountId.split('.');
      if (parts.length >= 2) {
        return parts[1]; // The second part is the tenant ID
      }
    }

    // Method 2: From account environment (if available)
    if (account.environment) {
      // This is less reliable but can be used as fallback
      console.log('Account environment:', account.environment);
    }

    // Method 3: From account localAccountId (if it contains tenant info)
    if (account.localAccountId) {
      console.log('Local Account ID:', account.localAccountId);
    }

    return '';
  }

  /**
   * Returns the tenant ID to use for API calls.
   * After customer selection, returns the selected customer's tenantId.
   * Falls back to the caller's own tenantId before customer selection completes.
   */
  getCurrentCustomerTenantId(): string {
    return this._activeCustomer.getValue()?.TenantId ?? this.CustomerTenantId;
  }

  /**
   * Extract customer tenant ID from JWT token claims
   * @param token - The JWT token (access token or ID token)
   * @returns The customer tenant ID from the token
   */
  getCustomerTenantIdFromToken(token: string): string {
    try {
      // JWT tokens have three parts separated by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid JWT token format');
        return '';
      }

      // Decode the payload (second part)
      const payload = parts[1];
      
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      
      // Decode base64
      const decodedPayload = atob(paddedPayload);
      
      // Parse JSON
      const claims = JSON.parse(decodedPayload);
      
      // Extract tenant ID from various possible claims
      return claims.tid || claims.tenant_id || claims.tenantId || '';
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return '';
    }
  }

  /**
   * Get customer tenant ID from the current access token
   * @returns Promise<string> - The customer tenant ID from the current access token
   */
  async getCustomerTenantIdFromCurrentToken(): Promise<string> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        console.error('No active account found');
        return '';
      }

      // Get access token silently
      const tokenRequest = {
        scopes: ['User.Read'], // or whatever scopes you need
        account: account
      };

      const response = await this.msalService.instance.acquireTokenSilent(tokenRequest);
      
      if (response.accessToken) {
        return this.getCustomerTenantIdFromToken(response.accessToken);
      }
      
      return '';
    } catch (error) {
      console.error('Error getting customer tenant ID from current token:', error);
      return '';
    }
  }

  /**
   * Get customer tenant ID from the current ID token
   * @returns The customer tenant ID from the current ID token
   */
  getCustomerTenantIdFromIdToken(): string {
    const account = this.msalService.instance.getActiveAccount();
    if (!account || !account.idToken) {
      console.error('No active account or ID token found');
      return '';
    }

    return this.getCustomerTenantIdFromToken(account.idToken);
  }

  /**
   * Get the current authentication provider
   * @returns string - The authentication provider ('microsoft', 'google', 'email', or '')
   */
  getAuthProvider(): string {
    return this.authProvider;
  }

  /**
   * Get the authenticated user's objectId across all auth providers.
   * - MSAL: first segment of homeAccountId (format: objectId.tenantId)
   * - Google/JWT: oid or sub claim from the stored access token
   * - Email/password: stored username (email) as stable identifier
   */
  getObjectId(): string {
    const account = this._activeAccount.getValue();
    if (account?.homeAccountId?.includes('.')) {
      return account.homeAccountId.split('.')[0];
    }

    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      const oid = this.jwtUtils.getObjectId(accessToken);
      if (oid) return oid;
    }

    return localStorage.getItem('username') || '';
  }

  /**
   * Check if user is authenticated with Google
   * @returns boolean - True if authenticated with Google
   */
  isGoogleAuthenticated(): boolean {
    return this.authProvider === 'google' && this._isAuthenticated.getValue();
  }

  /**
   * Check if user is authenticated with Microsoft
   * @returns boolean - True if authenticated with Microsoft
   */
  isMicrosoftAuthenticated(): boolean {
    return this.authProvider === 'microsoft' && this._isAuthenticated.getValue();
  }

  /**
   * Check if user is authenticated with email/password
   * @returns boolean - True if authenticated with email/password
   */
  isEmailAuthenticated(): boolean {
    return this.authProvider === 'email' && this._isAuthenticated.getValue();
  }

  /**
   * Get Google user information if available
   * @returns object with Google user data or null
   */
  getGoogleUserInfo(): { name: string; picture: string; email: string } | null {
    if (this.authProvider === 'google') {
      return {
        name: localStorage.getItem('googleUserName') || '',
        picture: localStorage.getItem('googleUserPicture') || '',
        email: localStorage.getItem('username') || ''
      };
    }
    return null;
  }
}
